// react
import { useMemo } from 'react';

// mui
import { MenuItem, TextField } from '@mui/material';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Icons } from '@/components/atoms/Icon';

// theme
import { Color } from '@/theme/color';

const PlaceholderForSelect = ({ placeholder }: { placeholder: string }) => {
  return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{placeholder}</span>;
};

/**
 * Select allows the user to select a value from a list of options.
 * Wraps MUI's TextField (select=true) so it picks up the current MUI variant
 * (outlined / filled / standard) from the surrounding theme. The label,
 * tooltip and supporting text rendering are still handled by BaseField above
 * the input, so MUI's internal label is intentionally not used.
 */
export const Select = ({
  className,
  id,
  label,
  description,
  tooltip,
  value,
  onChange,
  options,
  placeholder = 'Select',
  ariaLabel,
  isDisabled = false,
  isRequired = false,
  isHideTitle = false,
}: SelectProps) => {
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [options],
  );

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
      className={className}
    >
      <TextField
        select
        fullWidth
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={isDisabled}
        required={isRequired}
        slotProps={{
          htmlInput: {
            'aria-label': ariaLabel || label,
          },

          select: {
            displayEmpty: true,
            renderValue: (selected: unknown) => {
              if (!selected || typeof selected !== 'string') {
                return <PlaceholderForSelect placeholder={placeholder} />;
              }

              const selectedOption = sortedOptions.find(
                (o) => o.value === selected,
              );
              return selectedOption?.label ?? selected;
            },
            MenuProps: {
              slotProps: { paper: { style: { maxHeight: 360 } } },
            },
          },
        }}
      >
        {sortedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </BaseField>
  );
};

export interface Option {
  value: string;
  label: string;
  order?: number;
  icon?: Icons;
  iconFill?: Color;
}

export interface SelectProps extends FieldProps<string> {
  className?: string;
  options: Option[];
}

Select.componentName = 'Select';

export default Select;
